import React, { useCallback } from 'react'
import { Select, makeStyles, OutlinedInput, Box, InputLabel, FormControl, Typography } from '@material-ui/core'
import { gray, purple } from '../../utils/colors'
import { capitilizeMultipleWords } from '../../utils'
import StyledChip from './StyledChip'
import StyledIconWithTooltip from './StyledIconWithTooltip'

const useStyles = makeStyles(() => ({
	root: {
		display: 'flex',
		flexDirection: 'column',
		flex: 1,

		'& .MuiFormControl-root': {
			flex: 1,
			'& .MuiFormLabel-root': {
				zIndex: 'unset'
			},
			'& .MuiInputLabel-shrink': {
				transform: 'translate(12px, -5px) scale(0.75)'
			},
			'& .MuiInputBase-root > .MuiSelect-select.Mui-disabled': {
				backgroundColor: gray[200],
				borderTopColor: gray[500],
				borderLeftColor: gray[500],
				borderRightColor: gray[500],
				'& > div': {
					backgroundColor: gray[200]
				}
			}
		},
		'& .MuiInputBase-root': {
			margin: 0,
			flex: 1,
			minWidth: 'unset',
			borderBottomRightRadius: 0,
			borderBottomLeftRadius: 0,
			fontSize: '1rem',
			fontFamily: 'Nunito',
			'& .MuiSelect-icon.Mui-disabled': {
				color: gray[500],
				fill: gray[500]
			}
		},
		'& .MuiOutlinedInput-notchedOutline': {
			borderColor: gray[500],
			borderBottomColor: gray[600],
			borderBottomWidth: '1px'
		},
		'& .Mui-disabled .MuiOutlinedInput-notchedOutline': {
			borderBottomWidth: 1,
			borderColor: gray[500]
		},
		'& .Mui-focused .MuiOutlinedInput-notchedOutline': {
			borderColor: gray[500],
			borderWidth: 1,
			borderBottomColor: purple[500],
			borderBottomWidth: '2px'
		},
		'& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
			borderWidth: 1,
			borderColor: gray[500],
			borderBottomColor: gray[600],
			borderBottomWidth: '2px'
		}
	},
	headerContainer: {
		display: 'flex',
		alignItems: 'center',
		gap: '.5rem'
	}
}))

const StyledMultiSelect = ({
	label = '',
	header = '',
	values = [],
	menuItems = [],
	onValuesChanged,
	useChips = false,
	disabled = false,
	required = false,
	tooltip = null,
	chipLabelRenderFunction = null,
	singleSelect = false
}) => {
	const classes = useStyles()

	const handleOnValuesChanged = useCallback(
		(event) => {
			if (onValuesChanged) {
				onValuesChanged(event.target.value)
			}
		},
		[onValuesChanged]
	)

	return (
		<div className={classes.root}>
			{header ? (
				tooltip && !disabled ? (
					<span className={classes.headerContainer}>
						<Typography variant="overline">{header}</Typography>
						<StyledIconWithTooltip
							tooltipChildren={tooltip.tooltipChildren || undefined}
							tooltipText={tooltip.tooltipText || ''}
							faIcon={tooltip.faIcon || undefined}
							skiptownvariant={tooltip.skiptownvariant || undefined}
						/>
					</span>
				) : (
					<Typography variant="overline">{header}</Typography>
				)
			) : null}
			<FormControl>
				{label > '' ? <InputLabel>{`${label}${required ? ' *' : ''}`}</InputLabel> : null}
				<Select
					multiple={!singleSelect}
					required={required}
					value={values}
					disabled={disabled}
					onChange={handleOnValuesChanged}
					input={<OutlinedInput label={label} />}
					renderValue={(selected) => {
						if (Array.isArray(selected)) {
							if (useChips) {
								return (
									<Box
										sx={{
											display: 'flex',
											flexWrap: 'wrap',
											gap: 0.5
										}}>
										{selected.map((value) => {
											let label = value
											if (chipLabelRenderFunction) {
												label = chipLabelRenderFunction(value)
											}
											return <StyledChip key={value} label={label} />
										})}
									</Box>
								)
							} else {
								return selected.map((val) => capitilizeMultipleWords(val)).join(', ')
							}
						} else {
							if (useChips) {
								let label = selected
								if (chipLabelRenderFunction) {
									label = chipLabelRenderFunction(selected)
								}
								return (
									<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
										<StyledChip key={selected} label={label} />
									</Box>
								)
							} else {
								return selected
							}
						}
					}}>
					{menuItems}
				</Select>
			</FormControl>
		</div>
	)
}

export default StyledMultiSelect
